import {HttpClient} from '@angular/common/http';
import {Inject, Injectable} from '@angular/core';
import {Observable} from 'rxjs';

import {KeyGeneratorService} from '@atmi/core';
import {ResponsePaginateSuccess, ResponseSuccess} from '@atmi/core/models';
import {generateIndexQueryString, generatePlainQueryParamsFromObject, IndexQueryStringType} from '@atmi/core/utils';

import {addCollectionItem, deleteCollectionItem, setCollectionItem, updateCollectionItem} from '@src/app/store/collection-item.repository';
import {firstValueFrom} from 'rxjs';

import {first, map, tap} from 'rxjs/operators';
import {CollectionItem} from '../models/collection';
import {CollectionModuleConfiguration} from '../models/configuration';
import {CollectionConfigurationService} from '../token';

@Injectable({
  providedIn: 'root',
})
export class CollectionItemApiService {

  private readonly path = `${this.config.basePath}/collezioni`;

  constructor(
    @Inject(CollectionConfigurationService) private readonly config: CollectionModuleConfiguration,
    private readonly http: HttpClient,
    private readonly keyGeneratorService: KeyGeneratorService,
  ) {
  }

  async getAll() {
    try {
      const response = await firstValueFrom(this.http.get<ResponsePaginateSuccess<CollectionItem[]>>(`${this.path}`, {}));
      const {data, count} = response;

      const allCollectionItems: CollectionItem[] = [];
      for (const collection of data) {
        if (collection?.id) {
          try {
            const collectionItems = await firstValueFrom(this.index(collection.id));
            if (collectionItems && typeof collectionItems === 'object' && 'data' in collectionItems && collectionItems.data) {
              allCollectionItems.push(...(collectionItems.data));
            }
          } catch (error) {
            console.warn(`Failed to load items for collection ${collection.id}:`, error);
          }
        }
      }
      setCollectionItem(allCollectionItems);
    } catch (error) {
      // Handle error if needed
      console.warn('Failed to load collection items:', error);
      // Don't throw error to prevent app crash
    }
  }

  index(collectionId: string, queryString?: IndexQueryStringType): Observable<ResponsePaginateSuccess<CollectionItem[]>> {
    if (!collectionId) {
      console.warn('Collection ID is undefined, skipping API call');
      // Return an observable that emits empty data
      return new Observable(observer => {
        observer.next({
          data: [],
          count: 0,
          params: {
            filter: '',
            sort: '',
            skip: 0,
            limit: 10
          },
          statusCode: 200
        });
        observer.complete();
      });
    }
    const params = generateIndexQueryString(queryString);
    return this.http.get<ResponsePaginateSuccess<CollectionItem[]>>(`${this.path}/${collectionId}/oggetti`, {params});
  }

  get(collectionId: string, id: string) {
    return this.http.get<ResponseSuccess<CollectionItem>>(`${this.path}/${collectionId}/oggetti/${id}`).pipe(
      map((res) => res.data),
    );
  }

  post(collectionId: string, collectionItem: Partial<CollectionItem>) {
    return this.http.post<ResponseSuccess<CollectionItem>>(`${this.path}/${collectionId}/oggetti`, collectionItem).pipe(
      map((res) => res.data),
      tap((collectionItem) => {
        try {
          addCollectionItem(collectionItem);
        } catch (err) {
          console.error(err);
        }
        return collectionItem;
      }),
      first(),
    );
  }

  put(collectionId: string, id: string, collectionItem: Partial<CollectionItem>) {
    return this.http.put<ResponseSuccess<CollectionItem>>(
      `${this.path}/${collectionId}/oggetti/${id}`,
      collectionItem
    ).pipe(
      map((res) => res.data),
      tap((item) => {
        updateCollectionItem(id, item);
        return item;
      }),
      first(),
    );
  }

  remove(collectionId: string, id: string) {
    return this.http.delete(`${this.path}/${collectionId}/oggetti/${id}`)
      .pipe(tap(
        (collectionItem) => {
          deleteCollectionItem(id);
          return collectionItem;
        }
      ), first());
  }

  exportCollectionItem(collectionId: string, id: string, template: string = 'default', format: string = 'pdf') {
    const key = this.keyGeneratorService.getBinaryKeyGenerator();

    let url = `${this.path}/${collectionId}/oggetti/${id}/export?template=${template}&format=${format}`;
    if (key) {
      url += `&key=${key}`;
    }

    return this.http.get(url, {responseType: 'blob'});
  }

  exportList(
    collectionId: string,
    template: string = 'default',
    format: string = 'pdf',
    queryString: IndexQueryStringType,
    selection?: string[],
  ) {
    const key = this.keyGeneratorService.getBinaryKeyGenerator();
    const params = generatePlainQueryParamsFromObject({...queryString, selection});

    let url = `${this.path}/${collectionId}/oggetti/export?template=${template}&format=${format}&${params}`;
    if (key) {
      url += `&key=${key}`;
    }

    return this.http.get(url, {responseType: 'blob'});
  }
}
